import { Box, Tooltip, Typography } from '@mui/material'
import People from '@mui/icons-material/People'
import EventNote from '@mui/icons-material/EventNote'
import AccessTime from '@mui/icons-material/AccessTime'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Full-width footer strip for a clinic location card. Surfaces the page's
 * primary signal — how many patients are waiting right now — as a clear,
 * labelled band instead of a floating count badge.
 *
 * Renders nothing when the location has no bookings, matching the previous
 * "no badge when empty" behaviour.
 *
 * @param {number} waitingCount  patients currently waiting (subset of booked)
 * @param {number} bookingCount  total patients booked today
 * @param {string} earliestTime  ISO/UTC instant of the next appointment (optional)
 */
export default function WaitingPatientsStrip({
  waitingCount = 0,
  bookingCount = 0,
  earliestTime = null,
  showNextTime = true,
}) {
  if (!bookingCount || bookingCount <= 0) return null

  const hasWaiting = waitingCount > 0
  const nextTime = earliestTime ? dayjs.utc(earliestTime).local() : null
  const nextTimeValid = nextTime && nextTime.isValid()
  const nextTimeLabel = nextTimeValid ? nextTime.format('h:mm A') : null
  // A time in the past isn't an upcoming "next" appointment. If patients are still waiting it means
  // they have been waiting *since* that time (overdue); if nobody is waiting, a past time is stale
  // and we hide it entirely.
  const nextIsPast = nextTimeValid ? nextTime.isBefore(dayjs()) : false
  const showTime = showNextTime && Boolean(nextTimeLabel) && (!nextIsPast || hasWaiting)
  const timeColor = nextIsPast ? 'rgba(217, 119, 6, 0.95)' : 'rgba(37, 99, 235, 0.95)'

  const waitingColor = hasWaiting ? 'rgba(217, 119, 6, 0.95)' : 'rgba(100, 116, 139, 0.9)'
  const labelColor = 'rgba(71, 85, 105, 0.9)'
  const divider = '1px solid rgba(15, 23, 42, 0.06)'

  const segmentSx = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: { xs: 'center', sm: 'flex-start' },
    gap: 0.75,
    px: { xs: 1, sm: 1.5 },
    py: { xs: 0.875, sm: 1.125 },
  }
  const numberSx = { fontSize: { xs: '15px', sm: '17px' }, fontWeight: 700, lineHeight: 1 }
  const labelSx = { fontSize: { xs: '11px', sm: '12px' }, color: labelColor, whiteSpace: 'nowrap' }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderTop: '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: hasWaiting ? 'rgba(245, 158, 11, 0.07)' : 'rgba(248, 250, 252, 0.85)',
      }}
    >
      <Tooltip
        title={hasWaiting ? `${waitingCount} patient${waitingCount !== 1 ? 's' : ''} waiting right now` : 'No one waiting right now'}
        arrow
      >
        <Box sx={{ ...segmentSx, borderRight: divider }}>
          <People sx={{ fontSize: { xs: 16, sm: 18 }, color: waitingColor, flexShrink: 0 }} />
          <Typography component="span" sx={{ ...numberSx, color: waitingColor }}>
            {waitingCount}
          </Typography>
          <Typography component="span" sx={labelSx}>
            waiting now
          </Typography>
        </Box>
      </Tooltip>

      <Tooltip title={`${bookingCount} patient${bookingCount !== 1 ? 's' : ''} booked today`} arrow>
        <Box sx={{ ...segmentSx, borderRight: showTime ? divider : 'none' }}>
          <EventNote sx={{ fontSize: { xs: 16, sm: 18 }, color: 'rgba(100, 116, 139, 0.8)', flexShrink: 0 }} />
          <Typography component="span" sx={{ ...numberSx, color: 'rgba(15, 23, 42, 0.88)' }}>
            {bookingCount}
          </Typography>
          <Typography component="span" sx={labelSx}>
            booked
          </Typography>
        </Box>
      </Tooltip>

      {showTime && (
        <Tooltip
          title={nextIsPast ? `Waiting since ${nextTimeLabel}` : `Next appointment at ${nextTimeLabel}`}
          arrow
        >
          <Box sx={segmentSx}>
            <AccessTime sx={{ fontSize: { xs: 16, sm: 18 }, color: timeColor, flexShrink: 0 }} />
            <Typography
              component="span"
              sx={{ fontSize: { xs: '12px', sm: '13px' }, fontWeight: 700, color: timeColor, whiteSpace: 'nowrap' }}
            >
              {nextTimeLabel}
            </Typography>
            <Typography component="span" sx={{ ...labelSx, display: { xs: 'none', sm: 'inline' } }}>
              {nextIsPast ? 'since' : 'next'}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}
